import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card } from "@/components/ui/card";
import { 
  ArrowRight, 
  BarChart3, 
  BookOpen, 
  BrainCircuit, 
  Calendar, 
  CheckCircle2, 
  Compass, 
  FileText, 
  Lightbulb, 
  Megaphone, 
  MessageSquare, 
  Rocket, 
  Search, 
  Share2, 
  Target, 
  Users,
  ChevronRight,
  ArrowUpRight,
  Play,
  ExternalLink,
  Clock,
  Zap,
  LineChart,
  Award,
  Star,
  Sparkles,
  BarChart,
  PenTool,
  Layers,
  Gauge,
  Workflow,
  Repeat,
  Palette,
  Cpu
} from "lucide-react";

const Features = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState("research");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const detailedRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const isFeaturesInView = useInView(featuresRef, { once: true, amount: 0.2 });
  const isDetailedInView = useInView(detailedRef, { once: true, amount: 0.2 });
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Scroll-based animations
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.8]);
  
  // Handle mouse movement for spotlight effect
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // Toggle expanded items
  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.5 }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.5 }
    }
  };
  
  // Core features data
  const coreFeatures = [
    {
      icon: Target,
      title: "Audience Research",
      description: "Identify your ideal customers and understand their needs, preferences, and pain points."
    },
    {
      icon: BrainCircuit,
      title: "AI Content Generation",
      description: "Create high-quality, platform-optimized content in seconds with our advanced AI technology."
    },
    {
      icon: Sparkles,
      title: "Multi-Channel Optimization",
      description: "Automatically optimize your content for different platforms and distribution channels."
    },
    {
      icon: BarChart,
      title: "Performance Analytics",
      description: "Track and analyze your marketing performance with comprehensive, real-time dashboards."
    },
    {
      icon: Calendar,
      title: "Content Calendar",
      description: "Plan, schedule, and manage your content across multiple platforms from a single interface."
    },
    {
      icon: PenTool,
      title: "Brand Consistency",
      description: "Maintain consistent brand voice and messaging across all your marketing channels."
    }
  ];

  // Research features data
  const researchFeatures = [
    {
      id: "audience-analysis",
      icon: Users,
      title: "Audience Analysis",
      description: "Understand who your ideal customers are and what they care about most.",
      details: "Our AI analyzes your target market to identify key demographics, interests, and behaviors. This helps you create content that speaks directly to your audience's needs and desires."
    },
    {
      id: "keyword-research",
      icon: Search,
      title: "Keyword Research",
      description: "Discover the exact terms your audience is searching for online.",
      details: "Our advanced keyword research tool identifies high-value search terms with the perfect balance of search volume and competition, helping you optimize your content for maximum visibility."
    },
    {
      id: "competitor-analysis",
      icon: LineChart,
      title: "Competitor Analysis",
      description: "Learn what's working for your competitors and how to differentiate yourself.",
      details: "Analyze your competitors' content strategy, identify gaps in their approach, and discover opportunities to stand out in your market with unique, valuable content."
    },
    {
      id: "trend-detection",
      icon: Lightbulb,
      title: "Trend Detection",
      description: "Stay ahead of emerging topics and trends in your industry.",
      details: "Our AI constantly monitors social media, news sources, and search trends to identify emerging topics before they become mainstream, giving you a first-mover advantage."
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-sans">
      <SEOHead
        title="Features — Intelligent marketing for technical teams | LaunchGuide.io"
        description="LaunchGuide automates and optimizes user acquisition for technical founders and product teams: content, social, research, and more."
        pathname="/features"
      />
      <Navbar />
      
      {/* Hero Section */}
      <motion.section 
        ref={heroRef}
        className="relative py-24 md:py-32 overflow-hidden"
        style={{ opacity: heroOpacity }}
      >
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <motion.div 
          className="absolute top-1/4 right-[15%] w-96 h-96 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-[100px]"
          animate={{ 
            y: [0, 30, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 12, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute bottom-1/4 left-[10%] w-80 h-80 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-[100px]"
          animate={{ 
            y: [0, -40, 0],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate={isHeroInView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <motion.div 
              className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Zap className="w-4 h-4 mr-2" />
              Intelligent Marketing Features
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400"
              variants={fadeInUp}
            >
              Discover the Power of <span className="gradient-text">Intelligent Marketing</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed"
              variants={fadeInUp}
            >
              LaunchGuide automates and optimizes every facet of user acquisition for technical founders and product teams, helping you reach your audience more effectively.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Button 
                size="lg" 
                className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-xl"
                onMouseMove={handleMouseMove}
                asChild
              >
                <Link to="/auth/register">
                  <span className="relative z-10 flex items-center font-medium">
                    Start Your Free Trial
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="spotlight" style={{ 
                    '--x': `${mousePosition.x}px`,
                    '--y': `${mousePosition.y}px`,
                  } as React.CSSProperties}></span>
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent"></div>
      </motion.section>
      
      {/* Features Overview Section - REMOVED */}
      
      {/* Detailed Feature Sections - Redesigned */}
      <section 
        ref={detailedRef}
        className="py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900/50 dark:to-gray-950 relative overflow-hidden"
      >
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <motion.div 
          className="absolute top-1/3 right-[15%] w-96 h-96 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-[100px]"
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.div 
              className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <Target className="w-4 h-4 mr-2" />
              Research & Analysis
            </motion.div>
            
            <motion.h2 
              className="text-3xl md:text-4xl font-bold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Understand Your Audience <span className="gradient-text">Like Never Before</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our powerful research tools help you identify your ideal audience, understand their needs, and create content that resonates with them.
            </motion.p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInLeft}
              className="relative"
            >
              {/* Custom visualization instead of image */}
              <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800 p-6">
                {/* Audience Dashboard */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-lg font-semibold">Audience Insights Dashboard</div>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  
                  {/* Audience Segments Chart - Redesigned as a more abstract visual */}
                  <div className="mb-6">
                    <div className="text-sm font-medium mb-3">Audience Segments</div>
                    <div className="flex flex-col">
                      {/* Labels in a row first */}
                      <div className="grid grid-cols-4 gap-4 mt-3 mb-4">
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">Technical Leaders</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">Product Teams</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">Marketing</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">Other</div>
                      </div>
                      
                      {/* Bars on top of labels */}
                      <div className="grid grid-cols-4 gap-4 h-44 relative">
                        <motion.div 
                          className="w-full bg-blue-500 rounded-lg"
                          style={{ height: '80%' }}
                          initial={{ height: 0 }}
                          whileInView={{ height: '80%' }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.7, delay: 0.1 }}
                        />
                        
                        <motion.div 
                          className="w-full bg-blue-500 rounded-lg"
                          style={{ height: '60%' }}
                          initial={{ height: 0 }}
                          whileInView={{ height: '60%' }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.7, delay: 0.2 }}
                        />
                        
                        <motion.div 
                          className="w-full bg-blue-500 rounded-lg"
                          style={{ height: '45%' }}
                          initial={{ height: 0 }}
                          whileInView={{ height: '45%' }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.7, delay: 0.3 }}
                        />
                        
                        <motion.div 
                          className="w-full bg-pink-500 rounded-lg"
                          style={{ height: '25%' }}
                          initial={{ height: 0 }}
                          whileInView={{ height: '25%' }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.7, delay: 0.4 }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Interest Map */}
                  <div>
                    <div className="text-sm font-medium mb-3">Top Audience Interests</div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { topic: "Software Development", percentage: 85 },
                        { topic: "DevOps Tools", percentage: 78 },
                        { topic: "User Acquisition", percentage: 72 },
                        { topic: "Product Management", percentage: 65 }
                      ].map((item, index) => (
                        <motion.div 
                          key={index}
                          className="bg-gray-50 dark:bg-gray-800 rounded-md p-2"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.1 * index }}
                        >
                          <div className="text-xs font-medium mb-1">{item.topic}</div>
                          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                            <motion.div 
                              className="h-full bg-blue-500 rounded-full"
                              initial={{ width: 0 }}
                              whileInView={{ width: `${item.percentage}%` }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.7, delay: 0.2 + (0.1 * index) }}
                            ></motion.div>
                          </div>
                          <div className="text-right text-xs mt-1">{item.percentage}%</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Keywords Analysis */}
                <div>
                  <div className="text-sm font-medium mb-3">Trending Keywords</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { word: "Cloud Deployment", size: "text-xs", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
                      { word: "CI/CD", size: "text-lg", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
                      { word: "Developer Experience", size: "text-sm", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
                      { word: "Microservices", size: "text-base", color: "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300" },
                      { word: "API-first", size: "text-xs", color: "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300" },
                      { word: "Serverless", size: "text-lg", color: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300" },
                      { word: "Infrastructure", size: "text-sm", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
                      { word: "User Acquisition", size: "text-base", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" }
                    ].map((keyword, index) => (
                      <motion.span 
                        key={index}
                        className={`${keyword.size} ${keyword.color} px-3 py-1 rounded-full font-medium`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.05 * index }}
                      >
                        {keyword.word}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <motion.div 
                className="absolute -top-6 -right-6 w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center"
                animate={{ y: [0, -8, 0], rotate: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              >
                <Users className="h-6 w-6 text-blue-500" />
              </motion.div>
              
              <motion.div 
                className="absolute -bottom-6 -left-6 w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center"
                animate={{ y: [0, 8, 0], rotate: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              >
                <Search className="h-6 w-6 text-blue-500" />
              </motion.div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInRight}
            >
              <h3 className="text-2xl font-bold mb-6">
                Research & Analysis Features
              </h3>
              
              <div className="space-y-6">
                {researchFeatures.map((feature) => (
                  <div 
                    key={feature.id}
                    className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                    onClick={() => toggleExpanded(feature.id)}
                  >
                    <div className="flex items-start">
                      <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mr-4 flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-grow">
                        <h4 className="text-lg font-semibold mb-1">{feature.title}</h4>
                        <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
                        
                        <AnimatePresence>
                          {expandedItems.includes(feature.id) && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800"
                            >
                              <p className="text-gray-600 dark:text-gray-300">{feature.details}</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <ChevronRight 
                          className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${expandedItems.includes(feature.id) ? 'rotate-90' : ''}`} 
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8">
                <Button 
                  className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground"
                  onMouseMove={handleMouseMove}
                  asChild
                >
                  <Link to="/auth/register">
                    <span className="relative z-10 flex items-center">
                      Supercharge your audience insights
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                    <span className="spotlight" style={{ 
                      '--x': `${mousePosition.x}px`,
                      '--y': `${mousePosition.y}px`,
                    } as React.CSSProperties}></span>
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Content Generation Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-20 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <motion.div 
          className="absolute top-1/3 left-[10%] w-96 h-96 rounded-full bg-blue-400/5 dark:bg-blue-400/10 blur-[100px]"
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.div 
              className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              AI-Powered Content
            </motion.div>
            
            <motion.h2 
              className="text-3xl md:text-4xl font-bold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Generate <span className="gradient-text">High-Converting Content</span> in Minutes
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our advanced AI transforms your product insights into compelling content optimized for engagement and conversion across platforms.
            </motion.p>
          </div>
          
          {/* Content Generation Process Timeline */}
          <div className="mb-8">
            <div className="relative">
              {/* Horizontal connecting line */}
              <div className="absolute left-0 right-0 top-24 md:top-28 h-1 bg-gradient-to-r from-blue-500 to-blue-500 rounded-full hidden md:block"></div>
              
              {/* Process Steps */}
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { 
                    icon: BrainCircuit, 
                    title: "Input Analysis", 
                    description: "Our AI analyzes your product details, target audience, and marketing goals to understand the context.",
                    color: "indigo"
                  },
                  { 
                    icon: Lightbulb, 
                    title: "Content Strategy", 
                    description: "The system determines optimal content types, tones, and formats based on your specific needs.",
                    color: "blue"
                  },
                  { 
                    icon: PenTool, 
                    title: "AI Generation", 
                    description: "Advanced language models create multiple variations of engaging, conversion-focused content.",
                    color: "purple"
                  },
                  { 
                    icon: Sparkles, 
                    title: "Multi-Channel Delivery", 
                    description: "Content is automatically formatted and optimized for your selected distribution channels.",
                    color: "indigo"
                  }
                ].map((step, index) => (
                  <motion.div 
                    key={index}
                    className="relative"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {/* Step number with gradient border */}
                    <div className="relative mx-auto mb-6 z-10">
                      <div className={`w-14 h-14 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center text-xl font-bold border-2 border-${step.color}-500 relative z-20`}>
                        <div className={`absolute inset-0 bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 rounded-full opacity-10`}></div>
                        <span>{index + 1}</span>
                      </div>
                      <div className={`absolute inset-0 bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 rounded-full blur-md opacity-40`}></div>
                    </div>
                    
                    {/* Content */}
                    <div className={`bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 h-full`}>
                      <div className="flex items-center justify-center mb-4">
                        <div className={`w-12 h-12 rounded-lg bg-${step.color}-50 dark:bg-${step.color}-900/20 flex items-center justify-center`}>
                          <step.icon className={`h-6 w-6 text-${step.color}-600 dark:text-${step.color}-400`} />
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-center mb-3">{step.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300 text-center">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Content Examples Grid - Fixed layout */}
          <div className="grid md:grid-cols-2 gap-8 mt-28">
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24">
                  <div className="absolute transform rotate-45 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs uppercase font-bold py-1 right-[-35px] top-[32px] w-[140px] text-center">
                    Blog Content
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-4 mt-2">Compelling Blog Articles</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Generate in-depth, SEO-optimized blog posts that establish thought leadership and drive organic traffic.
                </p>
                
                <div className="mt-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-start mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-500 flex items-center justify-center text-white font-bold text-xs mr-3 flex-shrink-0">
                      AI
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1 text-sm">10 Advanced Strategies to Scale Your SaaS Product</h4>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Comprehensive guide with actionable insights for technical founders looking to grow their user base...
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-4">
                    <span>2,400 words</span>
                    <span>SEO-optimized</span>
                    <span>Conversion-focused</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="bg-white dark:bg-gray-900 rounded-xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24">
                  <div className="absolute transform rotate-45 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs uppercase font-bold py-1 right-[-35px] top-[32px] w-[140px] text-center">
                    Social Media
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-4 mt-2">Engaging Social Posts</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Create platform-specific social content designed to maximize engagement and drive traffic.
                </p>
                
                <div className="mt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-blue-500 flex items-center justify-center text-white text-xs mr-2">
                          <Target className="h-3 w-3" />
                        </div>
                        <span className="text-sm font-medium">LinkedIn</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        "Just launched our new developer tool that increases deployment speed by 70%! Here's how we built it... #DevTools #TechInnovation"
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 flex items-center justify-center text-white text-xs mr-2">
                          <Zap className="h-3 w-3" />
                        </div>
                        <span className="text-sm font-medium">Twitter</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        "🚀 We just cut our CI/CD pipeline from 45 mins to 3 mins. Here's the exact config we used (with code examples)..."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="flex justify-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Button 
              className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-xl"
              onMouseMove={handleMouseMove}
              asChild
            >
              <Link to="/auth/register">
                <span className="relative z-10 flex items-center font-medium">
                  Start Your Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </span>
                <span className="spotlight" style={{ 
                  '--x': `${mousePosition.x}px`,
                  '--y': `${mousePosition.y}px`,
                } as React.CSSProperties}></span>
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* Content Production Section */}
      <section className="py-24 bg-gray-50 dark:bg-gray-900/50 relative overflow-hidden">
        {/* Background patterns */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6">
                <FileText className="w-4 h-4 mr-2" />
                Content Production
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Streamlined <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Production Workflow</span>
              </h2>
              
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                A complete system for planning, creating, reviewing, and publishing content across all your marketing channels.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Calendar,
                    title: "Intelligent Content Calendar",
                    description: "AI-powered scheduling that determines optimal posting times based on audience behavior patterns."
                  },
                  {
                    icon: Workflow,
                    title: "Automated Workflow",
                    description: "Streamlined content production with customizable approval processes and team collaboration."
                  },
                  {
                    icon: Repeat,
                    title: "Cross-Channel Publishing",
                    description: "Publish to multiple platforms with a single click, with content automatically formatted for each channel."
                  }
                ].map((feature, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                        <feature.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10">
                <Button 
                  className="relative overflow-hidden group bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                  onMouseMove={handleMouseMove}
                  asChild
                >
                  <Link to="/auth/register">
                    <span className="relative z-10 flex items-center">
                      Scale your marketing efforts
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                    <span className="spotlight" style={{ 
                      '--x': `${mousePosition.x}px`,
                      '--y': `${mousePosition.y}px`,
                    } as React.CSSProperties}></span>
                  </Link>
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Content Dashboard Mockup */}
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl opacity-30 blur-xl"></div>
                
                {/* Dashboard frame */}
                <div className="relative bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 shadow-xl">
                  {/* Header bar */}
                  <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 text-center">
                      <div className="text-sm font-medium">Content Production Dashboard</div>
                    </div>
                    <div className="w-20"></div>
                  </div>
                  
                  {/* Dashboard content */}
                  <div className="p-6">
                    {/* Content calendar */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold mb-3">Upcoming Content Schedule</h4>
                      <div className="grid grid-cols-7 gap-2 mb-3">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                          <div key={i} className="text-center text-xs text-gray-500 dark:text-gray-400">{day}</div>
                        ))}
                        {Array.from({ length: 14 }).map((_, i) => (
                          <div 
                            key={i} 
                            className={`h-16 rounded-lg border border-gray-100 dark:border-gray-800 flex items-center justify-center text-xs ${
                              [2, 5, 9, 12].includes(i) 
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                                : ''
                            }`}
                          >
                            {i + 15}
                            {[2, 5, 9, 12].includes(i) && (
                              <div className="absolute w-2 h-2 rounded-full bg-blue-500 top-2 right-2"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Content items */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3">Content Pipeline</h4>
                      <div className="space-y-3">
                        {[
                          { title: 'How to Optimize Your CI/CD Pipeline', type: 'Blog Post', status: 'In Review', progress: 70 },
                          { title: 'Product Update: New API Features', type: 'Social Campaign', status: 'Scheduled', progress: 100 },
                          { title: 'Developer Productivity Guide', type: 'Lead Magnet', status: 'Drafting', progress: 30 },
                        ].map((item, i) => (
                          <div key={i} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex items-center">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{item.title}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{item.type}</div>
                            </div>
                            <div className="text-right">
                              <div className={`text-xs font-medium ${
                                item.status === 'Scheduled' ? 'text-green-500' :
                                item.status === 'In Review' ? 'text-yellow-500' : 'text-blue-500'
                              }`}>{item.status}</div>
                              <div className="w-24 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mt-1">
                                <div 
                                  className={`h-full rounded-full ${
                                    item.status === 'Scheduled' ? 'bg-green-500' :
                                    item.status === 'In Review' ? 'bg-yellow-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${item.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decoration elements */}
                <motion.div 
                  className="absolute -top-6 -right-6 w-12 h-12 bg-cyan-50 dark:bg-cyan-900/20 rounded-full flex items-center justify-center"
                  animate={{ 
                    y: [0, -8, 0],
                    rotate: [0, 10, 0]
                  }}
                  transition={{ 
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut" 
                  }}
                >
                  <Calendar className="h-6 w-6 text-cyan-500" />
                </motion.div>
                
                <motion.div 
                  className="absolute -bottom-6 -left-6 w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center"
                  animate={{ 
                    y: [0, 8, 0],
                    rotate: [0, -10, 0]
                  }}
                  transition={{ 
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5
                  }}
                >
                  <FileText className="h-6 w-6 text-blue-500" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonials/Trust Elements Section */}
      <section className="py-24 bg-gray-50 dark:bg-gray-900/50 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-20 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.div 
              className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Measurable Results
            </motion.div>
            
            <motion.h2 
              className="text-3xl md:text-4xl font-bold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Transform Your Marketing <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">Metrics</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our platform delivers real, measurable improvements to your marketing performance across all key metrics.
            </motion.p>
          </div>
          
          {/* Testimonial Cards */}
          <div className="relative">
            {/* Decorative elements */}
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-teal-400/5 dark:bg-teal-400/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-green-400/5 dark:bg-green-400/10 rounded-full blur-3xl"></div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  quote: "As a technical founder, I was spending too much time figuring out marketing. LaunchGuide automated 80% of my content creation process, letting me focus on what I do best—building my product.",
                  author: "Alex Chen",
                  role: "Founder, DevStack",
                  rating: 5
                },
                {
                  quote: "The AI-generated content is indistinguishable from what we'd get from an expensive marketing agency. We've seen a 3x increase in our organic traffic since implementing LaunchGuide.",
                  author: "Sarah Williams",
                  role: "CTO, Datalytics",
                  rating: 5
                },
                {
                  quote: "Our small team was struggling to maintain consistent marketing output alongside product development. LaunchGuide solved this completely, giving us enterprise-level marketing capabilities.",
                  author: "Michael Rodriguez",
                  role: "Product Lead, CodeSync",
                  rating: 5
                }
              ].map((testimonial, index) => (
                <motion.div 
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="relative bg-white dark:bg-gray-900 rounded-xl p-8 shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-all duration-300 h-full">
                    {/* Quote icon */}
                    <div className="absolute top-6 right-6 text-gray-200 dark:text-gray-700">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.1667 17.5H7.5C6.58333 17.5 5.83333 16.75 5.83333 15.8333V10.8333C5.83333 9.91667 6.58333 9.16667 7.5 9.16667H12.5C13.4167 9.16667 14.1667 9.91667 14.1667 10.8333V22.5C14.1667 27.5 10.8333 31.6667 5.83333 32.5V29.1667C8.33333 28.3333 10 25.8333 10 22.5V21.6667C10 20.75 9.25 20 8.33333 20C7.08333 20 5.83333 21.25 5.83333 22.5C5.83333 23.75 6.58333 24.1667 7.5 24.1667V29.1667C4.16667 28.3333 1.66667 25 1.66667 21.6667V10.8333C1.66667 7.5 4.16667 5 7.5 5H12.5C13.4167 5 14.1667 5.75 14.1667 6.66667V17.5ZM32.5 17.5H25.8333C24.9167 17.5 24.1667 16.75 24.1667 15.8333V10.8333C24.1667 9.91667 24.9167 9.16667 25.8333 9.16667H30.8333C31.75 9.16667 32.5 9.91667 32.5 10.8333V22.5C32.5 27.5 29.1667 31.6667 24.1667 32.5V29.1667C26.6667 28.3333 28.3333 25.8333 28.3333 22.5V21.6667C28.3333 20.75 27.5833 20 26.6667 20C25.4167 20 24.1667 21.25 24.1667 22.5C24.1667 23.75 25.4167 24.1667 26.6667 24.1667V29.1667C22.5 28.3333 20.8333 25 20.8333 21.6667V10.8333C20.8333 7.5 23.3333 5 26.6667 5H30.8333C31.75 5 32.5 5.75 32.5 6.66667V17.5Z" fill="currentColor"/>
                      </svg>
                    </div>
                    
                    {/* Star rating */}
                    <div className="flex mb-6">
                      {Array(testimonial.rating).fill(0).map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-300 mb-6 relative z-10">"{testimonial.quote}"</p>
                    
                    <div className="flex items-center">
                      {/* Abstract avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-green-500 flex items-center justify-center text-white font-bold text-lg mr-4">
                        {testimonial.author.split(' ').map(name => name[0]).join('')}
                      </div>
                      <div>
                        <div className="font-semibold">{testimonial.author}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/10 dark:from-blue-900/20 dark:to-blue-900/20"></div>
        
        {/* Background grid */}
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 dark:bg-[radial-gradient(#1f2937_1px,transparent_1px)]"></div>
        
        {/* Animated elements */}
        <motion.div 
          className="absolute top-1/4 left-[15%] w-64 h-64 rounded-full bg-blue-400/10 dark:bg-blue-400/20 blur-3xl"
          animate={{ 
            y: [0, 30, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute bottom-1/4 right-[15%] w-72 h-72 rounded-full bg-blue-400/10 dark:bg-blue-400/20 blur-3xl"
          animate={{ 
            y: [0, -20, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 12, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <motion.div 
              className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Rocket className="w-4 h-4 mr-2" />
              Get Started Today
            </motion.div>
            
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Ready to <span className="gradient-text">Transform</span> Your Marketing?
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Join thousands of technical founders and product teams who are using LaunchGuide to automate their marketing and accelerate user acquisition.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Button 
                size="lg" 
                className="relative overflow-hidden group bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-xl"
                onMouseMove={handleMouseMove}
                asChild
              >
                <Link to="/auth/register">
                  <span className="relative z-10 flex items-center font-medium">
                    Start Your Free Trial
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="spotlight" style={{ 
                    '--x': `${mousePosition.x}px`,
                    '--y': `${mousePosition.y}px`,
                  } as React.CSSProperties}></span>
                </Link>
              </Button>
            </motion.div>
            
            <motion.div 
              className="mt-8 text-sm text-gray-500 dark:text-gray-400 flex flex-wrap justify-center gap-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                <span>Free 14-day trial</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                <span>Cancel anytime</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Features;